import { captureSentryException } from '../sentry';
import { useGlobal } from './useGlobal';
import {
    Source,
    TargetLayer,
    WidgetType,
} from 'rwl-shared/dist/models/analytics';
import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useState,
} from 'react';

// Wrap the AnalyticsClientProvider and friends from sharedUI to use the Global context's configuration for easier implementation
import {
    AnalyticsClientProvider,
    AnalyticsSessionProvider,
    AnalyticsPropertyProvider,
    useAnalyticsSession,
    useAnalyticsLoadEvent,
    AnalyticsTargetLayerProvider,
    AnalyticsTargetLayerAreaProvider,
    AnalyticsClient,
} from 'rwl-ui';
import { useJob, useReview } from '../fetchers';
import { usePostCode } from './usePostCode';

interface AnalyticsProviderProps {
    pluginPropertyId?: string;
    client?: AnalyticsClient;
    widgetType?: WidgetType;
}

interface AnalyticsContextType {
    isReady: boolean;
}

const AnalyticsReadyContext = createContext<AnalyticsContextType | undefined>(
    undefined
);

export const AnalyticsProvider = ({
    pluginPropertyId,
    client,
    widgetType,
    children,
}: PropsWithChildren<AnalyticsProviderProps>): JSX.Element => {
    const { apiBaseUrl, accountId, config } = useGlobal();
    const [integration, setIntegration] = useState<string | undefined>(
        undefined
    );
    const [isReady, setIsReady] = useState<boolean>(false);

    useEffect(() => {
        if (!config) {
            return;
        }

        setIntegration(config.integration.id);
        setIsReady(true);
    }, [config]);

    const options = {
        onSendError: (error: Error) => {
            captureSentryException(error);
        },
    };

    const source = pluginPropertyId ? Source.PLUGIN : Source.STANDALONE;

    if (!widgetType) {
        throw new Error('widgetType is required for AnalyticsProvider');
    }

    // SessionProvider will create a session if one does not exist and the account is provided. We use this as an
    // escape hatch to prevent the session from being created before the config has loaded.
    return (
        <AnalyticsReadyContext.Provider value={{ isReady }}>
            <AnalyticsClientProvider
                client={client}
                baseUrl={apiBaseUrl}
                options={options}
            >
                <AnalyticsSessionProvider
                    account={isReady ? accountId : undefined} // See note above
                    source={source}
                    widgetType={isReady ? widgetType : undefined}
                    integration={integration}
                >
                    <AnalyticsPropertyProvider property={pluginPropertyId}>
                        <AnalyticsTargetLayerProvider
                            targetLayer={TargetLayer.WIDGET}
                        >
                            <AnalyticsTargetLayerAreaProvider>
                                {children}
                            </AnalyticsTargetLayerAreaProvider>
                        </AnalyticsTargetLayerProvider>
                    </AnalyticsPropertyProvider>
                </AnalyticsSessionProvider>
            </AnalyticsClientProvider>
        </AnalyticsReadyContext.Provider>
    );
};

export const useAnalytics = (): AnalyticsContextType => {
    const context = useContext(AnalyticsReadyContext);

    if (!context) {
        throw new Error(
            'useAnalytics must be used within an AnalyticsProvider'
        );
    }
    return context;
};

export const TriggerLoadEvent = (): null => {
    const { isReady } = useAnalytics();
    const { session } = useAnalyticsSession();
    const sendLoadEvent = useAnalyticsLoadEvent();

    useEffect(() => {
        if (isReady && session) {
            sendLoadEvent({});
        }
    }, [isReady, session]);

    return null;
};

// Reexport these providers/hooks from sharedUI for easy access
export {
    AnalyticsTargetLayerProvider,
    AnalyticsTargetLayerAreaProvider,
    useAnalyticsTargetLayer,
    useAnalyticsTargetLayerArea,
    useAnalyticsProperty,
    useAnalyticsSession,
    useAnalyticsClient,
} from 'rwl-ui';

// This hook is used to determine the target layer for the app layer
// This can change based on the current 'page' or 'view' the user is on
export const useAnalyticsAppLayer = (): TargetLayer => {
    const [appLayer, setAppLayer] = useState<TargetLayer>(TargetLayer.FEATURED);
    const { job, selectedMedia } = useJob();
    const { review } = useReview();
    const postCode = usePostCode();

    useEffect(() => {
        // Main non-closable views
        // The neighborhood/expanded view and the default featured view
        let layer = postCode ? TargetLayer.SERVICE_AREA : TargetLayer.FEATURED;
        // Details views, a closable view that shows more information about a job or review
        if (postCode) {
            layer = TargetLayer.SERVICE_AREA;
        }
        if (job || review) {
            layer = TargetLayer.JOB; // When reviews have their own details view, this will need to be updated
        }
        if (selectedMedia) {
            layer = TargetLayer.MEDIA_GALLERY;
        }
        setAppLayer(layer);
    }, [job, review, postCode, selectedMedia]);
    return appLayer;
};
